import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface QueuedAction {
  id: string;
  type: 'CREATE_READING' | 'UPDATE_READING' | 'DELETE_READING' | 'FAVORITE_READING' | 'PURCHASE_ITEM';
  endpoint: string;
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: any;
  timestamp: number;
  retryCount: number;
  status: 'pending' | 'syncing' | 'failed' | 'completed';
  error?: string;
}

interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  queue: QueuedAction[];
  lastSyncTime: number | null;
  
  // Actions
  setOnlineStatus: (isOnline: boolean) => void;
  addToQueue: (action: Omit<QueuedAction, 'id' | 'timestamp' | 'retryCount' | 'status'>) => void;
  removeFromQueue: (id: string) => void;
  updateActionStatus: (id: string, status: QueuedAction['status'], error?: string) => void;
  incrementRetryCount: (id: string) => void;
  clearQueue: () => void;
  startSync: () => void;
  endSync: () => void;
  setLastSyncTime: (time: number) => void;
}

export const useSyncStore = create<SyncState>()(
  persist(
    (set, get) => ({
      isOnline: navigator.onLine,
      isSyncing: false,
      queue: [],
      lastSyncTime: null,
      
      setOnlineStatus: (isOnline) => {
        set({ isOnline });
        
        // Trigger sync when coming back online
        if (isOnline && get().queue.length > 0 && !get().isSyncing) {
          // Dispatch custom event to trigger sync
          window.dispatchEvent(new CustomEvent('online-sync-trigger'));
        }
      },
      
      addToQueue: (action) => {
        const newAction: QueuedAction = {
          ...action,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: Date.now(),
          retryCount: 0,
          status: 'pending'
        };
        
        set((state) => ({
          queue: [...state.queue, newAction]
        }));
        
        // If online, trigger sync immediately
        if (get().isOnline && !get().isSyncing) {
          window.dispatchEvent(new CustomEvent('online-sync-trigger'));
        }
      },
      
      removeFromQueue: (id) => {
        set((state) => ({
          queue: state.queue.filter(action => action.id !== id)
        }));
      },
      
      updateActionStatus: (id, status, error) => {
        set((state) => ({
          queue: state.queue.map(action =>
            action.id === id
              ? { ...action, status, error }
              : action
          )
        }));
      },
      
      incrementRetryCount: (id) => {
        set((state) => ({
          queue: state.queue.map(action =>
            action.id === id
              ? { ...action, retryCount: action.retryCount + 1 }
              : action
          )
        }));
      },
      
      clearQueue: () => {
        set({ queue: [] });
      },
      
      startSync: () => {
        set({ isSyncing: true });
      },
      
      endSync: () => {
        set({ isSyncing: false });
      },
      
      setLastSyncTime: (time) => {
        set({ lastSyncTime: time });
      }
    }),
    {
      name: 'sync-storage',
      partialize: (state) => ({
        queue: state.queue,
        lastSyncTime: state.lastSyncTime
      })
    }
  )
);
